
import { defineComponent, PropType, nextTick } from 'vue';
import type { DropdownItems, DropdownSingleItem } from '@white-label-types/dropdown';
import DropdownList from '../dropdown-list/dropdown-list.vue';
import ErrorMessage from '../error-message/error-message.vue';
import { useScreenSize } from '@white-label-helper/shared-composables';

let componentIdCounter = 0;

export default defineComponent({
  name: 'DropdownWrapper',
  components: {
    DropdownList,
    ErrorMessage,
    IconS3: () => import('@white-label-icon/icons-s3'),
  },

  setup() {
    const { isDesktopResolution } = useScreenSize();
    return { isDesktopResolution };
  },

  props: {
    dropdownOpened: {
      type: Boolean,
      default: false,
    },
    disableCloseClickingOutside: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<DropdownItems>,
      required: true,
    },
    value: {
      type: Object as PropType<DropdownSingleItem>,
      required: true,
    },
    error: {
      type: Boolean,
      default: false,
      required: true,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    errorHtml: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    redErrorMsg: {
      type: Boolean,
      default: false,
    },
    matchWidth: {
      type: Boolean,
      default: false,
    },
    isSuccess: {
      type: Boolean,
      default: false,
    },
    // for when dropdown is a multi-select component and can be searched
    isSearchable: {
      type: Boolean,
      default: false,
    },
    focusOnClose: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      componentId: componentIdCounter++,
    };
  },

  computed: {
    localDropdownOpened: {
      get(): boolean {
        return this.dropdownOpened;
      },
      set(val: boolean) {
        this.$emit('update:dropdownOpened', val);
      },
    },

    computedAriaLabel(): string {
      const valueText = this.value?.name || this.placeholder;
      return `${this.label}: ${valueText}`;
    },

    localValue: {
      get(): string {
        return this.value?.name;
      },

      set(selectedItem: DropdownSingleItem) {
        this.$emit('input', selectedItem);
        this.localDropdownOpened = false;

        this.$nextTick(() => {
          const wrapper = this.$refs.dropdownWrapper as HTMLElement;
          if (wrapper && this.focusOnClose) {
            wrapper.focus();
          }
        })
      }
    },

    dropdownListboxId(): string {
      return `dropdown-listbox-${this.label.toLowerCase().replace(/\s+/g, '-')}`;
    },

    scopedSlots() {
      return this.$scopedSlots;
    },

    isDefaultSlotOverridden(): boolean {
      return !!this.$scopedSlots.default;
    },

    //for accessibility, creates unique ids for labels,listbox and errors
    labelId() {
      return `dropdown-label-${this.componentId}`;
    },
    listboxId() {
      return `dropdown-list-${this.componentId}`;
    },
    errorId() {
      return `dropdown-error-${this.componentId}`;
    },
  },

  watch: {
    // important for keyboard navigation - accessibility
    // watcher for shifting focus from wrapper to dropdown-list/customers-selector
    // finds first option that isn't disabled and focuses on it
    localDropdownOpened(isOpen, wasOpen) {
      if(isOpen) {
        if (this.isSearchable) {
          return;
        }

        this.$nextTick(() => {
          let firstOption = this.$el.querySelector('.dropdown-item');

          if (!firstOption) {
            firstOption = this.$el.querySelector<HTMLElement>('.num-selector, .num-selector__button:not(.num-selector__button--disabled), button.button-primary');
          }
          if (firstOption) {
            firstOption.focus();
          }
        })
      // for keeping focus on the dropdown wrapper when option is selected from the dropdown list
      } else if (wasOpen && this.isSearchable) {
        if (this.$el.contains(document.activeElement)) {
          this.$nextTick(() => {
            const wrapper = this.$refs.dropdownWrapper as HTMLElement;
            if (wrapper) {
              wrapper.focus();
            }
          });
        }
      }
    }
  },

  methods: {
    /**
     * Handles click on the dropdown target.
     * If the dropdown is searchable, it focuses the input field.
     * Otherwise, it toggles the dropdown visibility.
     */
    onClickDropdownTarget(event): void {
      if (this.readonly) {
        return;
      }

      this.$emit('focus');

      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA';

      if (isInputFocused && !(event instanceof MouseEvent && event.detail > 0)) {
        return;
      }

      if (this.isSearchable && (event as KeyboardEvent).key === 'Enter') {
        const input = this.$refs.dropdownWrapper.querySelector('input');
        if (input) {
          input.focus();
          this.localDropdownOpened = true;
        }
        return;
      }
      this.localDropdownOpened = !this.localDropdownOpened;
    },

    clickOutSide(): void {
      if ((this.isDesktopResolution || this.isSearchable) && !this.disableCloseClickingOutside) {
        this.localDropdownOpened = false;
      }
    },
    // for closing dropdown on key press and keeping focus on dropdown wrapper for accessibility
    closeDropdownAndFocusTarget(event: KeyboardEvent): void {
      if(this.localDropdownOpened) {
        event?.stopPropagation();
        event?.preventDefault();
        this.localDropdownOpened = false;

        nextTick(() => {
          const wrapper = this.$refs.dropdownWrapper as HTMLElement;
          if(wrapper) {
            wrapper.focus();
          }
        });
      }
    },

    //for allowing to use enter to select first item when searchable dropdown is open
    onEnterKey(event: KeyboardEvent): void {
      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA';

      if (isInputFocused && this.isSearchable && this.localDropdownOpened) {
        const firstItem = this.$el.querySelector('.dropdown-item') as HTMLElement;
        if (firstItem) {
          firstItem.click();
          return;
        }
      }
      this.onClickDropdownTarget(event);
    }
  },
});
